import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { DataSourcesService, type DataSourceCreate, type DataSourceUpdate } from '@/services/apiClient';
import { AgentProxyService } from '@/services/agentProxyService';
import { useMemo } from 'react';
import { useDataSourceFromStore } from './useDataSourceFromStore';
import { useDataSourceStore } from '@/stores/dataSourceStore';
// import { invalidateFetchUrl } from '@/utils/fetchDeduplication';

// Note: DedupDataSourceService removed - using centralized dataSourceStore instead

// Query keys
export const dataSourceKeys = {
  all: ['datasources'] as const,
  lists: () => [...dataSourceKeys.all, 'list'] as const,
  list: (filters: string) => [...dataSourceKeys.lists(), { filters }] as const,
  details: () => [...dataSourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...dataSourceKeys.details(), id] as const,
  infinite: () => [...dataSourceKeys.all, 'infinite'] as const,
};

// Get all data sources (traditional query)
export const useDataSourcesQuery = () => {
  return useQuery({
    queryKey: dataSourceKeys.lists(),
    queryFn: async () => {
      const response = await DataSourcesService.getDatasources({});
      return response.data || [];
    },
    enabled: (() => {
      // Only fetch if user is authenticated
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      return !!(token && userStr)
    })(),
    staleTime: 5 * 60 * 1000, // 5 minutes - list doesn't change frequently
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  });
};

// Get data sources with infinite pagination
export const useInfiniteDataSourcesQuery = (limit = 20) => {
  return useInfiniteQuery({
    queryKey: dataSourceKeys.infinite(),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await DataSourcesService.getDatasources({
        page: pageParam,
        limit,
      });
      return {
        data: response.data || [],
        pagination: response.pagination,
        nextPage: response.pagination?.has_next ? pageParam + 1 : undefined,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 1000, // 1 second stale time to prevent duplicate initial requests
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't auto-refetch on mount to prevent duplicates
    enabled: (() => {
      // Only fetch if user is authenticated
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      return !!(token && userStr)
    })(),
  });
};

// Get single data source using centralized store to prevent duplicate API calls
export const useDataSourceQuery = (id: string) => {
  // Normalize the ID to prevent unnecessary re-queries
  const normalizedId = useMemo(() => id?.trim() || '', [id]);
  
  // Use the centralized store hook
  return useDataSourceFromStore(normalizedId);
};

// Create data source
export const useCreateDataSourceMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (dataSource: DataSourceCreate) => {
      return DataSourcesService.createDatasources({
        requestBody: dataSource
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
    },
  });
};

// Update data source
export const useUpdateDataSourceMutation = () => {
  const queryClient = useQueryClient();
  const clearDataSource = useDataSourceStore((state) => state.clearDataSource);
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DataSourceUpdate }) => {
      return DataSourcesService.updateDatasources({
        datasourceId: id,
        requestBody: data
      });
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.detail(id) });
      // Clear from centralized store
      clearDataSource(id);
    },
  });
};

// Delete data source
export const useDeleteDataSourceMutation = () => {
  const queryClient = useQueryClient();
  const clearDataSource = useDataSourceStore((state) => state.clearDataSource);
  
  return useMutation({
    mutationFn: async (id: string) => {
      await DataSourcesService.deleteDatasources({
        datasourceId: id
      });
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
      queryClient.removeQueries({ queryKey: dataSourceKeys.detail(id) });
      // Clear from centralized store
      clearDataSource(id);
    },
  });
};

// Test data source connection
export const useTestDataSourceMutation = () => {
  return useMutation({
    mutationFn: async ({ datasourceId }: { 
      datasourceId: string, 
      testData?: {
        displayName?: string;
        databaseType?: 'MySQL' | 'PostgreSQL';
        host?: string;
        port?: number;
        username?: string;
        password?: string;
        defaultDatabase?: string | null;
        connectionParams?: Record<string, unknown>;
      }
    }) => {
      return AgentProxyService.testConnection(datasourceId);
    },
  });
};

// Update datasource order
export const useUpdateDataSourceOrderMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, newOrder }: { id: string; newOrder: number }) => {
      return DataSourcesService.updateDatasourceOrder({
        datasourceId: id,
        requestBody: { newOrder }
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
    },
  });
};

// Bulk reorder datasources
export const useBulkReorderDataSourcesMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      return DataSourcesService.bulkReorderDatasources({
        requestBody: { orderedIds }
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
    },
  });
};
